import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import "./i18n";
import "./styles/globals.css";

import { routeTree } from "./routeTree.gen";
import { NotFoundError, RateLimitedError } from "@/api/client";
import { loadRuntimeConfig } from "@/config";

// Customer portal query client — tuned for the public-lookup access pattern:
//   * Never retry 4xx (NotFoundError, RateLimitedError) — we map them to UX
//     states in the route. A retry on 404 would silently mask "user typed
//     a wrong serial".
//   * Up to 2 retries for transient network/5xx errors with exponential
//     backoff (cap 4s) — keeps the spinner short.
//   * 5-minute staleTime so refocus/refetch doesn't hammer the public API.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof NotFoundError) return false;
        if (error instanceof RateLimitedError) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  context: { queryClient },
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

/**
 * Boot-failure screen. Rendered when /config.json is missing or unusable —
 * i.e. a deployment fault, not a user fault. Kept as plain DOM (no i18n, no
 * component tree): at this point we cannot assume the app is functional, and
 * the audience is an operator reading a browser tab, not an end customer.
 */
function renderBootFailure(el: HTMLElement, message: string): void {
  const wrap = document.createElement("div");
  wrap.setAttribute("role", "alert");
  wrap.style.cssText =
    "max-width:42rem;margin:4rem auto;padding:0 1.5rem;font-family:system-ui,sans-serif;line-height:1.6";
  const h = document.createElement("h1");
  h.textContent = "Konfigurationsfehler";
  h.style.cssText = "font-size:1.25rem;font-weight:600;margin:0 0 .75rem";
  const p = document.createElement("p");
  p.textContent =
    "Dieses Portal ist nicht korrekt konfiguriert und kann keine Anfragen stellen. " +
    "Bitte informieren Sie den Betreiber.";
  p.style.cssText = "margin:0 0 1rem";
  const pre = document.createElement("pre");
  pre.textContent = message;
  pre.style.cssText =
    "white-space:pre-wrap;word-break:break-word;background:#f4f4f5;border:1px solid #e4e4e7;" +
    "border-radius:.5rem;padding:.75rem;font-size:.8125rem;margin:0";
  wrap.append(h, p, pre);
  el.replaceChildren(wrap);
}

// Config must resolve before the first render: buildUrl() reads it synchronously
// on every request. Failing here is deliberate and terminal — the alternative
// (falling back to some default endpoint) is exactly the bug this replaced.
loadRuntimeConfig()
  .then(() => {
    createRoot(rootEl).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </StrictMode>,
    );
  })
  .catch((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    console.error("[customer-portal] boot aborted:", error);
    renderBootFailure(rootEl, message);
  });
